import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AppShell } from "@/components/AppShell";
import { LeagueCard } from "@/components/LeagueCard";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Plus, LogIn, Trophy, Target, HelpCircle, Bell, Loader2, Coffee } from "lucide-react";
import { generateInviteCode } from "@/lib/utils-app";
import { toast } from "sonner";
import { PageMeta } from "@/components/PageMeta";
import { sortRanking } from "@/lib/ranking";
import { fetchAllPaginated } from "@/lib/fetchAll";
import { enablePush, hasActivePushSubscription, isPushSupported } from "@/lib/webPush";
import { AdSlot } from "@/components/ads/AdSlot";
import { InfoBanner } from "@/components/InfoBanner";

interface League {
  id: string;
  name: string;
  invite_code: string;
  owner_id: string;
  tournament_id: string | null;
  logo_url: string | null;
  is_premium: boolean;
}
interface Tournament {
  id: string;
  name: string;
  year: number;
  is_active: boolean;
}
interface LeagueStats {
  playerCount: number;
  myRank: number;
  myPoints: number;
  trend: number | null;
}

const Dashboard = () => {
  const { user, isPremium } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [leagueStats, setLeagueStats] = useState<Record<string, LeagueStats>>({});
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newTournament, setNewTournament] = useState<string>("");
  const [newExact, setNewExact] = useState<number>(3);
  const [newOutcome, setNewOutcome] = useState<number>(1);
  const [newLockMin, setNewLockMin] = useState<number>(0);
  const [newUnique, setNewUnique] = useState<boolean>(false);
  const [newTipsVisible, setNewTipsVisible] = useState<boolean>(true);
  const [newPremium, setNewPremium] = useState<boolean>(false);
  const [newGroupModule, setNewGroupModule] = useState<boolean>(false);
  const [joinCode, setJoinCode] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);
  const [pushSupported] = useState<boolean>(() => isPushSupported());
  const [pushActive, setPushActive] = useState<boolean>(false);
  const [pushBusy, setPushBusy] = useState<boolean>(false);
  const [pushDismissed, setPushDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("dashboard_push_cta_dismissed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!pushSupported) return;
    hasActivePushSubscription()
      .then(setPushActive)
      .catch(() => {});
  }, [pushSupported]);

  const enablePushFromDashboard = async () => {
    if (!user || pushBusy) return;
    if (!pushSupported) {
      toast.error(t("notifications.pushUnsupported"));
      return;
    }
    setPushBusy(true);
    try {
      await enablePush(user.id);
      setPushActive(true);
      // also persist preference flag so server sends push for this user
      const { data: profile } = await supabase
        .from("profiles")
        .select("notification_preferences")
        .eq("id", user.id)
        .maybeSingle();
      const prefs = (profile?.notification_preferences as any) ?? {};
      await supabase
        .from("profiles")
        .update({ notification_preferences: { ...prefs, push_enabled: true } })
        .eq("id", user.id);
      toast.success(t("notifications.pushEnabled"));
    } catch (e: any) {
      const msg = e?.message === "Permission denied" ? t("notifications.pushDenied") : (e?.message ?? "Error");
      toast.error(msg);
    } finally {
      setPushBusy(false);
    }
  };

  const dismissPushCta = () => {
    setPushDismissed(true);
    try {
      localStorage.setItem("dashboard_push_cta_dismissed", "1");
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    const [{ data: mems }, { data: ts }] = await Promise.all([
      supabase.from("league_members").select("league_id").eq("user_id", user!.id),
      supabase.from("tournaments").select("*").eq("is_active", true).order("year", { ascending: false }),
    ]);
    const activeTs = (ts as Tournament[]) ?? [];
    setTournaments(activeTs);
    if (activeTs.length && !newTournament) setNewTournament(activeTs[0].id);
    const ids = (mems ?? []).map((m) => m.league_id);
    if (ids.length === 0) {
      setLeagues([]);
      setLeagueStats({});
      setLoading(false);
      return;
    }
    const fetchAll = fetchAllPaginated;
    const [{ data }, allMembers, allPreds, allGroupPreds] = await Promise.all([
      supabase.from("leagues").select("*").in("id", ids).order("created_at", { ascending: false }),
      fetchAll<{ league_id: string; user_id: string }>(() =>
        supabase.from("league_members").select("league_id,user_id").in("league_id", ids).order("league_id").order("user_id"),
      ),
      fetchAll<{
        league_id: string;
        user_id: string;
        points_awarded: number | null;
        match_id: string;
        predicted_home_score: number;
        predicted_away_score: number;
      }>(() =>
        supabase
          .from("predictions")
          .select("league_id,user_id,points_awarded,match_id,predicted_home_score,predicted_away_score")
          .in("league_id", ids)
          .order("id"),
      ),
      fetchAll<{ league_id: string; user_id: string; points_awarded: number | null }>(() =>
        supabase.from("group_predictions").select("league_id,user_id,points_awarded").in("league_id", ids).order("id"),
      ),
    ]);

    const matchIds = Array.from(new Set((allPreds ?? []).map((p) => p.match_id)));
    const { data: allMatches } = matchIds.length
      ? await supabase.from("matches").select("id,kickoff_at,status,home_score,away_score").in("id", matchIds)
      : {
          data: [] as {
            id: string;
            kickoff_at: string;
            status: string;
            home_score: number | null;
            away_score: number | null;
          }[],
        };
    const matchMap = new Map<
      string,
      { kickoff_at: string; status: string; home_score: number | null; away_score: number | null }
    >(
      (allMatches ?? []).map((m: any) => [
        m.id,
        { kickoff_at: m.kickoff_at, status: m.status, home_score: m.home_score, away_score: m.away_score },
      ]),
    );
    const leaguesData = (data as League[]) ?? [];
    const leagueById = new Map(leaguesData.map((l) => [l.id, l as any]));
    const dayKey = (iso: string) => iso.slice(0, 10);
    const stats: Record<string, LeagueStats> = {};
    for (const lid of ids) {
      const members = (allMembers ?? []).filter((m) => m.league_id === lid);
      const playerCount = members.length;
      const leaguePreds = (allPreds ?? []).filter((p) => p.league_id === lid);
      const lg = leagueById.get(lid);
      const includeGroups = !!(lg?.is_premium && lg?.group_module_enabled);
      const leagueGroupPreds = includeGroups ? (allGroupPreds ?? []).filter((g: any) => g.league_id === lid) : [];
      // finished matchday boundary
      const finishedDays = new Set<string>();
      leaguePreds.forEach((p) => {
        const m = matchMap.get(p.match_id);
        if (m && m.status === "finished") finishedDays.add(dayKey(m.kickoff_at));
      });
      const sortedDays = Array.from(finishedDays).sort();
      const lastDay = sortedDays[sortedDays.length - 1];
      const memberIds = new Set(members.map((m) => m.user_id));
      const computeRank = (includePred: (p: (typeof leaguePreds)[number]) => boolean) => {
        const pointsByUser: Record<string, number> = {};
        const exactByUser: Record<string, number> = {};
        members.forEach((m) => {
          pointsByUser[m.user_id] = 0;
          exactByUser[m.user_id] = 0;
        });
        leaguePreds.forEach((p) => {
          if (!memberIds.has(p.user_id)) return;
          if (includePred(p)) {
            pointsByUser[p.user_id] = (pointsByUser[p.user_id] || 0) + (p.points_awarded || 0);
            const m = matchMap.get(p.match_id);
            if (
              m &&
              m.home_score !== null &&
              m.away_score !== null &&
              p.predicted_home_score === m.home_score &&
              p.predicted_away_score === m.away_score
            ) {
              exactByUser[p.user_id] = (exactByUser[p.user_id] || 0) + 1;
            }
          }
        });
        // Group points always count (no day filter — historic ranking is approximate)
        leagueGroupPreds.forEach((g: any) => {
          if (!memberIds.has(g.user_id)) return;
          pointsByUser[g.user_id] = (pointsByUser[g.user_id] || 0) + (g.points_awarded || 0);
        });
        const sorted = sortRanking(
          Object.keys(pointsByUser).map((uid) => ({
            user_id: uid,
            points: pointsByUser[uid] || 0,
            exact: exactByUser[uid] || 0,
          })),
        );
        const r = sorted.findIndex((row) => row.user_id === user!.id) + 1;
        const myPts = pointsByUser[user!.id] || 0;
        return { rank: r || 1, points: myPts };
      };
      const { rank: myRank, points: myPoints } = computeRank(() => true);
      let trend: number | null = null;
      if (sortedDays.length >= 2 && lastDay) {
        const prev = computeRank((p) => {
          const m = matchMap.get(p.match_id);
          return !!m && m.status === "finished" && dayKey(m.kickoff_at) < lastDay;
        });
        trend = prev.rank - myRank; // positive = moved up
      }
      stats[lid] = { playerCount, myRank, myPoints, trend };
    }
    setLeagues(leaguesData);
    setLeagueStats(stats);
    setLoading(false);
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateCreate = () => {
    const e: Record<string, string> = {};
    if (!newName.trim()) e.name = t("dashboard.nameRequired");
    if (!newTournament) e.tournament = t("dashboard.pickTournament");
    if (newExact < 0) e.exact = t("dashboard.exactPtsInvalid");
    if (newOutcome < 0) e.outcome = t("dashboard.outcomePtsInvalid");
    if (newLockMin < 0) e.lock = t("dashboard.lockMinInvalid");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const createLeague = async () => {
    if (!validateCreate()) return;
    const { data, error } = await supabase
      .from("leagues")
      .insert({
        name: newName.trim(),
        invite_code: generateInviteCode(),
        owner_id: user!.id,
        tournament_id: newTournament,
        exact_score_points: Math.max(0, newExact),
        correct_outcome_points: Math.max(0, newOutcome),
        lock_minutes_before_kickoff: Math.max(0, newLockMin),
        unique_results: newUnique,
        tips_visible_to_others: newTipsVisible,
        is_premium: isPremium && newPremium,
        group_module_enabled: isPremium && newPremium && newGroupModule,
      })
      .select()
      .single();
    if (error) return toast.error(error.message);
    toast.success(t("dashboard.leagueCreated"));
    setOpenCreate(false);
    setNewName("");
    setErrors({});
    navigate(`/league/${data.id}`);
  };

  const joinLeague = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    const { data: leagueId, error } = await supabase.rpc("join_league_with_code", { _code: code });
    if (error) {
      const msg = (error.message ?? "").toLowerCase();
      if (msg.includes("already a member")) {
        const { data: preview } = await supabase.rpc("get_league_preview_by_code", { _code: code });
        const row = Array.isArray(preview) ? preview[0] : preview;
        if (row?.id) {
          toast.info(t("dashboard.alreadyMember"));
          setOpenJoin(false);
          setJoinCode("");
          navigate(`/league/${row.id}`);
          return;
        }
      }
      return toast.error(t("dashboard.leagueNotFound"));
    }
    if (!leagueId) return toast.error(t("dashboard.leagueNotFound"));
    toast.success(t("dashboard.joined"));
    setOpenJoin(false);
    setJoinCode("");
    navigate(`/league/${leagueId}`);
  };

  return (
    <AppShell>
      <PageMeta
        title="Moje ligy — Tipomatic"
        description="Tvůj přehled tipovacích lig: pořadí, nadcházející zápasy a tipy kamarádů."
        path="/"
      />
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("dashboard.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("dashboard.subtitle")}</p>
        </div>
        <Button variant="ghost" className="mt-0.5 shrink-0 gap-1" asChild aria-label={t("help.title")}>
          <Link to="/help">
            {t("help.title")}
            <HelpCircle className="h-5 w-5" />
          </Link>
        </Button>
      </div>

      {pushSupported && !pushActive && !pushDismissed && (
        <Card className="mb-4 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="flex items-start gap-3 p-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <Bell className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{t("notifications.pushTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("notifications.pushDesc")}</p>
              <Button size="sm" className="mt-2 h-9" onClick={enablePushFromDashboard} disabled={pushBusy}>
                {pushBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("notifications.pushEnable")}
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="shrink-0" onClick={dismissPushCta}>
              {t("notifications.pushDismiss")}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="mb-6 flex gap-2">
        <Dialog open={openJoin} onOpenChange={setOpenJoin}>
          <DialogTrigger asChild>
            <Button variant="outline" className="flex-1 h-11">
              <LogIn className="h-4 w-4 mr-2" />
              {t("dashboard.join")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("dashboard.joinTitle")}</DialogTitle>
              <DialogDescription>{t("dashboard.joinDesc")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>{t("dashboard.invite")}</Label>
              <Input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={10}
                className="uppercase tracking-widest font-mono h-11"
              />
            </div>
            <DialogFooter>
              <Button onClick={joinLeague}>{t("dashboard.join")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={openCreate}
          onOpenChange={(open) => {
            setOpenCreate(open);
            if (open) setErrors({});
          }}
        >
          <DialogTrigger asChild>
            <Button className="flex-1 h-11 bg-gradient-pitch">
              <Plus className="h-4 w-4 mr-2" />
              {t("dashboard.newLeague")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("dashboard.createTitle")}</DialogTitle>
              <DialogDescription>{t("dashboard.createDesc")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>{t("dashboard.leagueName")}</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={60}
                  className={errors.name ? "h-11 border-red-500" : "h-11"}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>{t("dashboard.tournament")}</Label>
                {tournaments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("dashboard.noTournament")}</p>
                ) : (
                  <Select value={newTournament} onValueChange={setNewTournament}>
                    <SelectTrigger className={errors.tournament ? "h-11 border-red-500" : "h-11"}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tournaments.map((tr) => (
                        <SelectItem key={tr.id} value={tr.id}>
                          {tr.name} · {tr.year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.tournament && <p className="text-xs text-red-500">{errors.tournament}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("league.exactPts")}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newExact}
                    onChange={(e) => setNewExact(parseInt(e.target.value) || 0)}
                    className={errors.exact ? "h-11 border-red-500" : "h-11"}
                  />
                  {errors.exact && <p className="text-xs text-red-500">{errors.exact}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t("league.outcomePts")}</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(parseInt(e.target.value) || 0)}
                    className={errors.outcome ? "h-11 border-red-500" : "h-11"}
                  />
                  {errors.outcome && <p className="text-xs text-red-500">{errors.outcome}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("league.lockMinutesBefore")}</Label>
                <Input
                  type="number"
                  min={0}
                  value={newLockMin}
                  onChange={(e) => setNewLockMin(Math.max(0, parseInt(e.target.value) || 0))}
                  className={errors.lock ? "h-11 border-red-500" : "h-11"}
                />
                {errors.lock && <p className="text-xs text-red-500">{errors.lock}</p>}
                <p className="text-xs text-muted-foreground">{t("league.lockMinutesBeforeHint")}</p>
              </div>
              <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
                <div className="space-y-1">
                  <Label htmlFor="new-unique-results" className="cursor-pointer">
                    {t("league.uniqueResults")}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t("league.uniqueResultsHint")}</p>
                </div>
                <Switch id="new-unique-results" checked={newUnique} onCheckedChange={setNewUnique} />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
                <div className="space-y-1">
                  <Label htmlFor="new-tips-visible" className="cursor-pointer">
                    {t("league.tipsVisible")}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t("league.tipsVisibleHint")}</p>
                </div>
                <Switch id="new-tips-visible" checked={newTipsVisible} onCheckedChange={setNewTipsVisible} />
              </div>

              <div className="rounded-xl border border-amber-300/40 bg-gradient-to-br from-amber-50/60 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="new-premium" className="cursor-pointer flex items-center gap-2">
                      <PremiumBadge label="Premium liga" />
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {isPremium
                        ? "Bonus za unikátní přesný tip + tipování postupujících ze skupin."
                        : "Premium ligu může vytvořit pouze premium uživatel."}
                    </p>
                  </div>
                  <Switch id="new-premium" checked={newPremium} onCheckedChange={setNewPremium} disabled={!isPremium} />
                </div>
                {newPremium && isPremium && (
                  <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card/50 p-2.5">
                    <div className="space-y-0.5">
                      <Label htmlFor="new-group-module" className="cursor-pointer text-sm">
                        Modul: tipování skupin
                      </Label>
                      <p className="text-xs text-muted-foreground">Tip 1. a 2. místa ve skupinách.</p>
                    </div>
                    <Switch id="new-group-module" checked={newGroupModule} onCheckedChange={setNewGroupModule} />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button onClick={createLeague} disabled={tournaments.length === 0}>
                {t("dashboard.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <InfoBanner />

      {loading ? (
        <p className="text-muted-foreground">{t("common.loading")}</p>
      ) : leagues.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-pitch text-primary-foreground">
              <Trophy className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-semibold">{t("dashboard.noLeague")}</h3>
            <p className="text-muted-foreground max-w-sm text-sm">{t("dashboard.noLeagueDesc")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {leagues.map((l) => {
            const stats = leagueStats[l.id];
            return (
              <LeagueCard
                key={l.id}
                id={l.id}
                name={l.name}
                invite_code={l.invite_code}
                isAdmin={l.owner_id === user?.id}
                isPremium={l.is_premium}
                logo_url={l.logo_url}
                playerCount={stats?.playerCount}
                myRank={stats?.myRank}
                myPoints={stats?.myPoints}
                trend={stats?.trend ?? null}
              />
            );
          })}
          {leagues.length >= 2 && (
            <Link to="/predict">
              <Card className="border-dashed border-2 transition hover:shadow-elevated active:scale-[0.99] h-full flex flex-col items-center justify-center gap-2 py-6 cursor-pointer">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-accent text-accent-foreground">
                  <Target className="h-5 w-5" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-sm">{t("dashboard.bulkPredict")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("dashboard.bulkPredictDesc")}</p>
                </div>
              </Card>
            </Link>
          )}
          <a
            href="https://www.buymeacoffee.com/incubator"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="border-dashed border-2 transition hover:shadow-elevated active:scale-[0.99] h-full flex flex-col items-center justify-center gap-2 py-6 cursor-pointer">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-[#FFDD00] text-black">
                <Coffee className="h-5 w-5" />
              </div>
              <div className="text-center px-4">
                <p className="font-semibold text-sm">{t("help.bmcButton")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("help.bmcBody")}</p>
              </div>
            </Card>
          </a>
        </div>
      )}
      <AdSlot placement="dashboard-feed" />
    </AppShell>
  );
};

export default Dashboard;
